/* Native Brand Profile hydration. Shopify owns the vendor spelling, product
   count and product grid. Public Supabase dictionaries add verified founder,
   social and brand-story metadata without renaming the Shopify vendor. */

(function () {
  var DIRECTORY_CACHE_KEY = 'gettmi:brand-directory:v2';
  var PROFILE_CACHE_KEY = 'gettmi:brand-profiles:v1';

  function config() {
    var node = document.querySelector('[data-gettmi-supabase]');
    return node ? { url: node.dataset.supabaseUrl, key: node.dataset.supabaseKey } : null;
  }

  function normalizeBrandKey(value) {
    return String(value || '')
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  function slugifyBrand(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function formatSocialCount(value) {
    var number = Number(value);
    if (!Number.isFinite(number)) return '';
    if (number >= 1000000) return (number / 1000000).toFixed(number >= 10000000 ? 0 : 1) + 'M';
    if (number >= 1000) return (number / 1000).toFixed(number >= 100000 ? 0 : 1) + 'K';
    return String(number);
  }

  function readCache(key) {
    try {
      return JSON.parse(window.sessionStorage.getItem(key) || 'null');
    } catch (error) {
      return null;
    }
  }

  function writeCache(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      /* Public editorial data can still render without a cache. */
    }
  }

  function publicRead(cfg, path, cacheKey) {
    var cached = readCache(cacheKey);
    if (Array.isArray(cached)) return Promise.resolve(cached);
    return window.fetch(cfg.url + path, {
      headers: {
        apikey: cfg.key,
        Authorization: 'Bearer ' + cfg.key
      }
    }).then(function (response) {
      if (!response.ok) return [];
      return response.json();
    }).then(function (rows) {
      var result = Array.isArray(rows) ? rows : [];
      if (result.length) writeCache(cacheKey, result);
      return result;
    }).catch(function () {
      return [];
    });
  }

  function findDirectoryEntry(rows, brandName) {
    var target = normalizeBrandKey(brandName);
    return rows.find(function (row) {
      return [row.canonical_vendor, row.display_name].concat(row.shopify_vendors || []).some(function (name) {
        return normalizeBrandKey(name) === target;
      });
    }) || null;
  }

  function findBrandProfile(rows, brandName) {
    var target = normalizeBrandKey(brandName);
    return rows.find(function (row) {
      return [row.display_name, row.vendor, row.handle].filter(Boolean).some(function (name) {
        return normalizeBrandKey(name) === target;
      });
    }) || null;
  }

  function setText(mount, selector, value) {
    var node = mount.querySelector(selector);
    if (node) node.textContent = String(value || '');
  }

  function setAction(mount, selector, url) {
    var action = mount.querySelector(selector);
    if (!action) return;
    if (!url) {
      action.removeAttribute('href');
      action.setAttribute('aria-disabled', 'true');
      return;
    }
    action.href = url;
    action.target = '_blank';
    action.rel = 'noopener noreferrer';
    action.removeAttribute('aria-disabled');
  }

  function renderAvatar(mount, url, name) {
    var image = mount.querySelector('[data-gettmi-brand-avatar]');
    var fallback = mount.querySelector('[data-gettmi-brand-avatar-fallback]');
    fallback.textContent = String(name || 'G').trim().charAt(0).toUpperCase() || 'G';
    if (!url) return;
    image.src = url;
    image.alt = name;
    image.hidden = false;
    fallback.hidden = true;
  }

  function render(mount, directoryRows, profileRows) {
    var vendor = mount.dataset.vendor || 'GETTMI';
    var directory = findDirectoryEntry(directoryRows, vendor) || {};
    var profile = findBrandProfile(profileRows, directory.display_name || vendor) || {};
    var editorial = profile.raw_fields && typeof profile.raw_fields === 'object' ? profile.raw_fields : {};
    var founderMode = (editorial.profile_display_mode || 'Founder Profile') !== 'Brand Story';
    var brandCopy = directory.brand_copy || profile.intro || ('Shop the latest ' + vendor + ' pieces on Gettmi.');
    setText(mount, '[data-gettmi-brand-copy]', brandCopy);

    if (!founderMode) return;

    var social = mount.querySelector('[data-gettmi-brand-social]');
    var bioLines = String(profile.instagram_bio || directory.instagram_bio || '')
      .split(/\n+/)
      .map(function (line) { return line.trim(); })
      .filter(Boolean);
    var founderName = editorial.founder_name || bioLines[0] || vendor;
    var founderRole = editorial.founder_role || '';
    var supplementalLines = bioLines.filter(function (line) {
      var lower = line.toLowerCase();
      return lower !== String(founderName).toLowerCase() && lower !== String(founderRole).toLowerCase();
    }).slice(0, founderRole ? 2 : 3);
    var bio = [];
    if (founderRole) bio.push(founderRole);
    bio = bio.concat(supplementalLines);
    var instagramHandle = profile.instagram_handle || directory.instagram_handle || slugifyBrand(vendor);
    var instagramUrl = profile.instagram_url || directory.instagram_url || '';
    var complete = Boolean(
      directory.founder_profile_complete ||
      (profile.instagram_handle && profile.instagram_url && profile.instagram_bio)
    );
    var posts = complete ? formatSocialCount(profile.posts_count) : '';

    setText(mount, '[data-gettmi-brand-handle]', instagramHandle);
    setText(mount, '[data-gettmi-brand-founder]', founderName);
    setText(mount, '[data-gettmi-brand-bio]', bio.join('\n'));
    if (posts) {
      setText(mount, '[data-gettmi-brand-posts]', posts);
      mount.querySelector('[data-gettmi-brand-posts-wrap]').hidden = false;
    }

    var verified = mount.querySelector('[data-gettmi-brand-verified]');
    verified.hidden = !complete;

    var followers = formatSocialCount(profile.followers_count);
    var following = formatSocialCount(profile.following_count);
    if (followers) {
      setText(mount, '[data-gettmi-brand-followers]', followers);
      mount.querySelector('[data-gettmi-brand-followers-wrap]').hidden = false;
    }
    if (following) {
      setText(mount, '[data-gettmi-brand-following]', following);
      mount.querySelector('[data-gettmi-brand-following-wrap]').hidden = false;
    }

    renderAvatar(
      mount,
      profile.avatar_url || directory.founder_image_url || mount.dataset.fallbackImage || '',
      founderName
    );
    setAction(mount, '[data-gettmi-brand-follow]', instagramUrl);
    social.hidden = false;
  }

  function run() {
    var mount = document.querySelector('[data-gettmi-brand-profile]');
    var cfg = config();
    if (!mount || !cfg || !cfg.url || !cfg.key) return;

    var vendor = mount.dataset.vendor || '';
    var vendorQuery = encodeURIComponent(vendor);
    var cacheSuffix = ':' + normalizeBrandKey(vendor);
    var directorySelect = '/rest/v1/app_brand_metadata?' +
      'select=brand_profile_id,canonical_vendor,display_name,shopify_vendors,brand_copy,founder_image_url,founder_profile_complete,instagram_handle,instagram_url,instagram_bio' +
      '&order=display_name.asc';
    var profileSelect = '/rest/v1/brand_profiles?' +
      'select=id,avatar_url,display_name,followers_count,following_count,handle,instagram_bio,instagram_handle,instagram_url,intro,is_active,posts_count,raw_fields,status,vendor' +
      '&is_active=eq.true';
    var scopedDirectoryPath = directorySelect +
      '&or=(canonical_vendor.eq.' + vendorQuery + ',display_name.eq.' + vendorQuery + ')';
    var scopedProfilePath = profileSelect +
      '&or=(vendor.eq.' + vendorQuery + ',display_name.eq.' + vendorQuery + ',handle.eq.' + vendorQuery + ')';

    Promise.all([
      publicRead(cfg, scopedDirectoryPath, DIRECTORY_CACHE_KEY + cacheSuffix),
      publicRead(cfg, scopedProfilePath, PROFILE_CACHE_KEY + cacheSuffix)
    ]).then(function (results) {
      if (results[0].length) return results;
      return publicRead(cfg, directorySelect, DIRECTORY_CACHE_KEY).then(function (directoryRows) {
        return [directoryRows, results[1]];
      });
    }).then(function (results) {
      var directory = findDirectoryEntry(results[0], vendor);
      if (results[1].length || !directory || !directory.brand_profile_id) return results;
      var profileByIdPath = profileSelect + '&id=eq.' + encodeURIComponent(directory.brand_profile_id);
      return publicRead(cfg, profileByIdPath, PROFILE_CACHE_KEY + ':id:' + directory.brand_profile_id).then(function (profileRows) {
        return [results[0], profileRows];
      });
    }).then(function (results) {
      render(mount, results[0], results[1]);
    });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-gettmi-brand-back]');
    if (!button) return;
    var mount = button.closest('[data-gettmi-brand-profile]');
    if (window.history.length > 1) window.history.back();
    else window.location.assign(mount.dataset.homeUrl || '/');
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();
